import React, { ChangeEvent } from "react";
import { RouteComponentProps } from "react-router-dom";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import Checkbox from "../Checkbox";


interface IStatementPrintingViewState {
    isLoading: boolean | string;

    queuedItemCount: number;
    availableItemCount: number;

    printConsent: boolean;
}

interface IStatementPrintingViewProps extends RouteComponentProps<any> {

}

class StatementPrintingView extends React.Component<IStatementPrintingViewProps, IStatementPrintingViewState> {
    private _timer?: number;

    constructor(props: Readonly<IStatementPrintingViewProps>) {
        super(props);

        const state: IStatementPrintingViewState = {
            isLoading: true,

            queuedItemCount: 0,
            availableItemCount: 0,

            printConsent: false,
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.loadData = this.loadData.bind(this);
        this.queueAllForPrint = this.queueAllForPrint.bind(this);
        this.removeAllFromQueue = this.removeAllFromQueue.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public componentWillUnmount() {
        if (this._timer) {
            window.clearTimeout(this._timer);
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <section>
                    Current queue: <b>{!this.state.queuedItemCount ? "Empty" : `${this.state.queuedItemCount.toLocaleString()} items`}</b>
                </section>
                &nbsp;
                <section>
                    There are <b>{this.state.availableItemCount.toLocaleString()}</b> statement(s) available to be printed.
                </section>
                &nbsp;
                {
                    this.state.availableItemCount > 20
                        ? <section className="status-box">
                            <span>To queue this many statements to print, you must check the following box:</span><br />
                            <br />
                            <Checkbox id="frm-print-consent" fancy={true} className="white" onChange={this.handleElementChange} name="printConsent" checked={this.state.printConsent} />
                            <label htmlFor="frm-print-consent">Yes, I do intend to print <b>{this.state.availableItemCount.toLocaleString()}</b> statements</label>
                        </section>
                        : null
                }
                <div className="action-panel-dialog">
                    <button type="button" className="button text-with-icon" onClick={this.queueAllForPrint} disabled={!this.state.availableItemCount || (this.state.availableItemCount > 20 && !this.state.printConsent)}><i className="icon-print" /><span>Queue all for printing</span></button>
                    <button type="button" className="button text-with-icon" onClick={this.removeAllFromQueue} disabled={!this.state.queuedItemCount}><i className="icon-times" /><span>Remove queued items</span></button>
                </div>
                &nbsp;
                {
                    this.state.isLoading === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isLoading === "string"
                            ? <div className="error-text">{this.state.isLoading}</div>
                            : null
                }
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox"
            ? (e.currentTarget as HTMLInputElement).checked
            : e.currentTarget.value;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadData());
    }

    private async loadData() {
        try {
            if (this._timer)
                window.clearTimeout(this._timer);

            const [queuedItemCount, availableItemCount] = await Promise.all([
                V2.get<any>(`statements/count?status=printqueued&can_email=false`),
                V2.get<any>(`statements/count?status=notsent&can_email=false`),
            ]);

            this.setState({
                isLoading: false,

                queuedItemCount: queuedItemCount.count,
                availableItemCount: availableItemCount.count
            });

            this._timer = window.setTimeout(() => this.loadData(), 30000);
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load statements"
            });
        }
    }

    private async queueAllForPrint() {
        try {
            await V2.post("statements/queue/print/enqueue-all");

            this.setState({
                isLoading: false
            });

            await this.loadData();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not queue statements"
            });
        }
    }

    private async removeAllFromQueue() {
        try {
            await V2.post("statements/queue/print/dequeue-all");

            this.setState({
                isLoading: false
            });

            await this.loadData();
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not queue statements"
            });
        }
    }
}

export default withTitle("Statement Printing", StatementPrintingView);